import { useEffect, useState } from "react";
import DashboardLayout from "../../layoutcontainers/DashboardLayout";
import {
  Avatar,
  Box,
  Card,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import MaterialTypography from "../../components/MaterialTypography";
import MaterialButton from "../../components/MaterialButton";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { BASE_URL } from "../Utils/Baseurl";
import axios from "axios";
import AddIcon from "@mui/icons-material/Add";
import { useLoading } from "../../context/loadingcontext";
import noData from "../../assest/NoDataImage.png";
import { useAuth } from "../../context/AuthContext";
import ChatBot from "../chatbot";

const Project = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { setCirleLoading } = useLoading();
  const [project, setProject] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const open = Boolean(anchorEl);
  const [chatBotStatus, setChatBotStatus] = useState(false);
  const [suggestion, setSuggestion] = useState("");

  useEffect(() => {
    fetchProjectData();
    fetchChatList();
  }, []);

  const fetchChatList = async () => {
    setCirleLoading(true);
    try {
      const token = sessionStorage.getItem("token");
      const res = await axios.post(
        `${BASE_URL}ai/ManagerProjectAnalysis/`,
        { user_id: currentUser.id },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setChatBotStatus(true);
      setSuggestion(res?.data?.analysis || res?.data.message);
    } catch (error) {
      setSuggestion("");
      setChatBotStatus(false);
    } finally {
      setCirleLoading(false);
    }
  };

  /**
   * Fetches a summary of all projects from the API and updates the state with the retrieved data.
   * Retrieves the authentication token from session storage and includes it in the request headers.
   * Upon successful response, sets the project state with the fetched data.
   * Logs any errors encountered during the fetch operation.
   */
  const fetchProjectData = async () => {
    setCirleLoading(true);
    try {
      const token = sessionStorage.getItem("token");
      const response = await axios.post(
        `${BASE_URL}api/projects-summary/`,
        { user_id: currentUser },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = response.data.data;
      setProject(data);
    } catch (error) {
      console.log(error, "error");
    } finally {
      setCirleLoading(false);
    }
  };

  /**
   * Deletes a project from the API based on the selectedProjectId.
   * Retrieves the authentication token from session storage and includes it in the request headers.
   * Upon successful response, logs the response data.
   * Logs any errors encountered during the delete operation.
   */
  const projectDeleteData = async () => {
    try {
      const token = sessionStorage.getItem("token");
      const response = await axios.delete(`${BASE_URL}api/deleteProject/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          id: selectedProjectId,
        },
      });
      navigate("/project-list");
      fetchProjectData();
    } catch (error) {
      console.log(error, "error");
    } finally {
      setCirleLoading(false);
    }
  };

  const handleClick = (event, projectId) => {
    setAnchorEl(event.currentTarget);
    setSelectedProjectId(projectId);
  };

  const handleClose = (action) => {
    if (action && selectedProjectId) {
      if (action === "View") {
        navigate(`/project-list/view-project`, {
          state: { projectId: selectedProjectId },
        });
      } else if (action === "Edit") {
        navigate("/project-list/add-project/", {
          state: { projectId: selectedProjectId },
        });
      } else if (action === "Delete") {
        projectDeleteData();
      }
    }
    setAnchorEl(null);
    setSelectedProjectId(null);
  };

  const handleAddProject = () => {
    navigate("/project-list/add-project");
  };

  const truncate = (desc) =>
    desc?.length > 25 ? `${desc.substring(0, 140)}...` : desc;

  return (
    <DashboardLayout>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
        p={1}
        mb={2}
      >
        <Stack direction="column" spacing={0}>
          <MaterialTypography variant="h5" fontWeight="bold" color="dark">
            Project
          </MaterialTypography>
          <MaterialTypography variant="caption" color="text">
            You can view the projects here.
          </MaterialTypography>
        </Stack>
        <MaterialButton
          variant="contained"
          color="dark"
          onClick={handleAddProject}
          startIcon={<AddIcon />}
        >
          Add Project
        </MaterialButton>
      </Stack>
      {chatBotStatus && (
        <div className="chatbox-container">
          <ChatBot
            suggestionText={suggestion}
            style={{ marginTop: "-100px", marginRight: "180px" }}
          />
        </div>
      )}
      {project.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "60vh",
            textAlign: "center",
          }}
        >
          <img
            src={noData}
            alt="No Projects"
            style={{ width: "250px", height: "25    0px" }}
          />
        </Box>
      ) : (
        <Grid container spacing={2} sx={{ mt: 2, p: 1 }}>
          {project.map((item) => (
            <Grid size={{ xs: 12, sm: 3, md: 3 }} key={item.project_id}>
              <Card sx={{ p: 1, maxHeight: 240, zIndex: 0 }}>
                <Box sx={{ position: "absolute", top: 3, right: 3 }}>
                  <IconButton
                    onClick={(event) => handleClick(event, item.project_id)}
                    size="small"
                  >
                    <MoreVertIcon />
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={open && selectedProjectId === item.project_id}
                    onClose={() => handleClose("")}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "right",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                  >
                    <MenuItem onClick={() => handleClose("Edit")}>
                      Edit
                    </MenuItem>
                    <MenuItem onClick={() => handleClose("View")}>
                      View
                    </MenuItem>
                    <MenuItem onClick={() => handleClose("Delete")}>
                      Delete
                    </MenuItem>
                  </Menu>
                </Box>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 12 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Avatar
                        alt="Remy Sharp"
                        src={item.project_logo}
                        sx={{
                          width: 40,
                          height: 40,
                          "& img": {
                            objectFit: "cover",
                            width: "100%",
                            height: "100%",
                          },
                        }}
                      />
                      <Stack direction="column" spacing={0}>
                        <Typography
                          variant="h6"
                          fontWeight="bold"
                          color="text.primary"
                          sx={{ fontSize: "12px", cursor: "pointer" }}
                        // onClick={() =>
                        //   navigate(`/project/view-project`, {
                        //     state: { projectId: item.project_id },
                        //   })
                        // }
                        >
                          {item.project_name}
                        </Typography>

                        <MaterialTypography
                          variant="caption"
                          color="text"
                          sx={{ fontSize: "10px" }}
                        >
                          {item.project_code}
                        </MaterialTypography>
                      </Stack>
                    </Stack>
                  </Grid>
                </Grid>
                <Grid container spacing={2} mt={1}>
                  <Grid size={{ xs: 12, md: 12 }}>
                    <MaterialTypography
                      variant="body2"
                      color="text"
                      sx={{ fontSize: "10px" }}
                    >
                      {truncate(item.description)}
                    </MaterialTypography>
                  </Grid>
                </Grid>
                <Grid container spacing={2} mt={1}>
                  <Grid size={{ xs: 12, md: 12 }}>
                    <Stack direction="row" spacing={1}>
                      <Stack direction="column" spacing={0}>
                        <MaterialTypography
                          variant="h6"
                          fontWeight="bold"
                          color="text"
                          sx={{ fontSize: "10px" }}
                        >
                          {item.manager}
                        </MaterialTypography>
                        <MaterialTypography
                          variant="h6"
                          fontWeight="bold"
                          color="text"
                          sx={{ fontSize: "10px" }}
                        >
                          Project Manager
                        </MaterialTypography>
                      </Stack>
                    </Stack>
                  </Grid>
                </Grid>
                <Grid container spacing={2} mt={1}>
                  <Grid size={{ xs: 6, lg: 6 }}>
                    <Stack direction="column" spacing={0}>
                      <MaterialTypography
                        variant="h6"
                        fontWeight="bold"
                        color="text"
                        sx={{ fontSize: "10px" }}
                      >
                        Start Date
                      </MaterialTypography>
                      <MaterialTypography
                        variant="h6"
                        fontWeight="bold"
                        color="text"
                        sx={{ fontSize: "10px" }}
                      >
                        {item.start_date}
                      </MaterialTypography>
                    </Stack>
                  </Grid>
                  <Grid
                    size={{ xs: 6, lg: 6 }}
                    sx={{ display: "flex", flexDirection: "row-reverse" }}
                  >
                    <Stack direction="column" spacing={0}>
                      <MaterialTypography
                        variant="h6"
                        fontWeight="bold"
                        color="text"
                        sx={{ fontSize: "10px" }}
                      >
                        Live Date
                      </MaterialTypography>
                      <MaterialTypography
                        variant="h6"
                        fontWeight="bold"
                        color="text"
                        sx={{
                          fontSize: "10px",
                          color:
                            item.days_left < 30 && item.completion_percent < 50
                              ? "red"
                              : item.days_left < 5 &&
                                item.completion_percent < 80
                                ? "orange"
                                : "green",
                          animation:
                            item.days_left < 30 && item.completion_percent < 50
                              ? "none"
                              : "blinker 3s linear infinite",
                          "@keyframes blinker": {
                            "50%": {
                              opacity: 0,
                            },
                          },
                        }}
                      >
                        {item.end_date}
                      </MaterialTypography>
                    </Stack>
                  </Grid>
                </Grid>
                <Grid container spacing={1} mt={1}>
                  <Grid size={{ xs: 6, lg: 6 }}>
                    <Stack direction="row" spacing={0} alignItems="center">
                      <CalendarMonthIcon
                        sx={{ color: "#00C853", fontSize: 16 }}
                      />
                      <MaterialTypography
                        variant="body2"
                        color="text"
                        sx={{ fontSize: "10px" }}
                      >
                        {item.days_left} days left
                      </MaterialTypography>
                    </Stack>
                  </Grid>
                  <Grid
                    size={{ xs: 6 }}
                    sx={{ display: "flex", flexDirection: "row-reverse" }}
                  >
                    <Stack direction="column" spacing={0}>
                      <MaterialTypography
                        variant="body2"
                        color="text"
                        sx={{ fontSize: "10px" }}
                      >
                        Completed tasks: {item.completion_percent}%
                      </MaterialTypography>
                    </Stack>
                  </Grid>
                </Grid>
                {item?.status && (
                  <Box
                    sx={{
                      width: "100px",
                      height: "100px",
                      overflow: "hidden",
                      position: "absolute",
                      top: 0,
                      right: 0,
                      zIndex: -1,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontSize: "10px",
                        fontWeight: "bold",
                        color: "#fff",
                        textAlign: "center",
                        position: "absolute",
                        width: "140px",
                        top: "25px",
                        right: "-40px",
                        backgroundColor: item?.color || "#7A7A73",
                        padding: "2px 0",
                        transform: "rotate(45deg)",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                      }}
                    >
                      {item?.status}
                    </Box>
                  </Box>
                )}
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </DashboardLayout>
  );
};

export default Project;
